import eventBus from '@/utils/eventBus';

// Gate debug popups via localStorage or env var
const isEnabled = () => {
  try {
    const v = localStorage.getItem('debugPopups') ?? import.meta.env.VITE_DEBUG_POPUPS;
    return ['1', 'true', 'yes', 'on'].includes(String(v).toLowerCase());
  } catch {
    return false;
  }
};

export function setDebugPopupsEnabled(enabled = true) {
  try { localStorage.setItem('debugPopups', enabled ? '1' : '0'); } catch {}
}

/**
 * Dispatch a debug notification (shown as a PrimeVue toast via DebugNotifier)
 * @param {string} summary - short title
 * @param {any} detailOrOptions - string/object detail or full toast options
 * @param {object} [options] - additional toast options
 */
export function debugNotify(summary, detailOrOptions, options = {}) {
  if (!isEnabled()) return;
  let payload = {
    severity: 'info',
    summary: String(summary ?? 'Debug'),
    life: 12000, // longer default so you can read it
    closable: true,
  };
  if (typeof detailOrOptions === 'string') {
    payload.detail = detailOrOptions;
  } else if (detailOrOptions && typeof detailOrOptions === 'object') {
    // Pretty print objects by default
    const { detail, ...rest } = detailOrOptions;
    payload = { ...payload, ...rest };
    if (detail != null) {
      payload.detail = typeof detail === 'string' ? detail : safeStringify(detail);
    }
  }
  if (options && typeof options === 'object') {
    payload = { ...payload, ...options };
  }
  // Sticky option support: if sticky is set, extend life significantly
  if (payload.sticky === true) {
    payload.life = 60000; // 60s
  }
  // Ensure detail is a string for toast
  if (payload.detail == null) payload.detail = '';
  if (typeof payload.detail !== 'string') payload.detail = safeStringify(payload.detail);
  try {
    eventBus.emit('debug', payload);
  } catch {}
}

export function debugNotifyJSON(label, obj) {
  if (!isEnabled()) return;
  debugNotify(String(label || 'Debug'), { detail: obj });
}

function safeStringify(obj) {
  try { return JSON.stringify(obj, replacer, 2); } catch { return String(obj); }
}
function replacer(key, value) {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  return value;
}

// Convenience globals for quick toggling/testing in DevTools
if (typeof window !== 'undefined') {
  window.debugNotify = debugNotify;
  window.__debugPopups = (enable = true) => setDebugPopupsEnabled(!!enable);
}

export default debugNotify;
